import {
  compareAvailabilityStates,
  getPrioritizedAvailabilityState as getPrioritizedAvailabilityStateLib
} from "../../../../commons/libs/availability";
import { SpecFormatter } from "../../../../commons/libs/formatters";
import { getProductImageUrl } from "../../../../commons/libs/resource-paths";
import { getAllSpecValues } from "../../../../commons/libs/specs";
import { ActiveFilters } from "../../../../commons/specs/filters";
import { Product, ProductKey, ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { AssortmentType } from "../../../../commons/types/assortment";
import {
  AvailabilityState,
  AvailabilityStatus,
  AvailabilityType,
  ManualAssortmentAvailabilityState
} from "../../../../commons/types/availability";
import { Currency } from "../../../../commons/types/currency";
import { ImageManipulationFormat } from "../../../../commons/types/image-manipulation";
import { MatchedMediaQueryBreakpoints } from "../../../../commons/types/media-queries";
import { AssortmentPriceSettings, CustomizationSettings } from "../../../../commons/types/settings";
import Availability from "../../components/Availability/Availability";
import ProductTile from "../../components/ProductTile/ProductTile";
import { OriginalPricePartial, PricePartial, SaleBadgePartial } from "../partials/PricePartials";

interface Props<Type extends ProductType> {
  product: Product<Type>;
  onClick: () => void;
  brandKey?: string;
  breakpoints: MatchedMediaQueryBreakpoints;
  activeFilters: ActiveFilters<Type>;
  customization: CustomizationSettings;
  currency: Currency;
  assortmentType: AssortmentType | null;
  assortmentPriceSettings: AssortmentPriceSettings;
  specFormatter: SpecFormatter<Type>;
}

const ProductTilePartial = <Type extends ProductType>({
  product,
  onClick,
  brandKey,
  activeFilters,
  breakpoints,
  customization,
  currency,
  assortmentType,
  assortmentPriceSettings,
  specFormatter
}: Props<Type>) => {
  const renderAvailability = (product: Product) => {
    const getAvailabilityState = (): AvailabilityState | null => {
      if (assortmentType === AssortmentType.Manual) {
        return getManualAssortmentAvailability(product);
      }

      if (assortmentType === AssortmentType.Automatic) {
        return getAutomaticAssortmentAvailability(product);
      }

      return null;
    };

    const availabilityState = getAvailabilityState();
    return availabilityState ? <Availability kind={availabilityState.type} {...availabilityState} /> : undefined;
  };

  const getManualAssortmentAvailability = (product: Product): AvailabilityState | null => {
    const availability = product[ProductSpecKey.ManualAssortment];
    if (!availability) {
      return null;
    }

    return availability === ManualAssortmentAvailabilityState.Current
      ? { type: AvailabilityType.Full, status: AvailabilityStatus.InStock }
      : { type: AvailabilityType.No, status: AvailabilityStatus.NotInStock };
  };

  const getAutomaticAssortmentAvailability = (product: Product): AvailabilityState | null => {
    const combinedAssortmentFilters = activeFilters?.[ProductSpecKey.CombinedAssortment] || [];

    const hasActiveWawiAssortmentFilter =
      combinedAssortmentFilters.filter(value => value && `${value}`.startsWith("wawi_")).length > 0;

    const hasActiveVeloconnectAssortmentFilter =
      combinedAssortmentFilters.filter(value => value && `${value}`.startsWith("veloconnect_")).length > 0;

    const hasBothActiveAssortmentFilters = hasActiveWawiAssortmentFilter && hasActiveVeloconnectAssortmentFilter;

    if (hasBothActiveAssortmentFilters) {
      return getPrioritizedCombinedAvailabilityState(product);
    } else if (hasActiveWawiAssortmentFilter) {
      return getPrioritizedAvailabilityState(product, ProductSpecKey.WawiAvailability);
    } else if (hasActiveVeloconnectAssortmentFilter) {
      return getPrioritizedAvailabilityState(product, ProductSpecKey.VeloconnectAvailability);
    } else {
      return null;
    }
  };

  const getPrioritizedCombinedAvailabilityState = (product: Product): AvailabilityState | null => {
    const availabilityStates = getAllSpecValues(ProductSpecKey.Availability, product);
    return availabilityStates.length > 0 ? availabilityStates.toSorted(compareAvailabilityStates)[0] : null;
  };

  const getPrioritizedAvailabilityState = (
    product: Product,
    availabilityKey: ProductSpecKey.WawiAvailability | ProductSpecKey.VeloconnectAvailability
  ): AvailabilityState | null => {
    const availabilityStates = getAllSpecValues(availabilityKey, product).map(availabilityStates =>
      getPrioritizedAvailabilityStateLib(availabilityStates, customization.availabilityPrioritizationOptions)
    );
    return availabilityStates.length > 0 ? availabilityStates.toSorted(compareAvailabilityStates)[0] : null;
  };

  const pricePartialProps = {
    productOrVariant: product,
    customization,
    currency,
    assortmentPriceSettings,
    specFormatter
  };

  return (
    <ProductTile
      size={breakpoints.xl ? "default" : "s"}
      showPrices={customization.showPrices}
      brandName={!brandKey ? product[ProductSpecKey.BrandName] : undefined}
      key={product[ProductKey.ProductId]}
      title={specFormatter.formatProductValue(ProductSpecKey.ModelName, product) ?? ""}
      price={<PricePartial {...pricePartialProps} />}
      originalPrice={<OriginalPricePartial {...pricePartialProps} />}
      badge={<SaleBadgePartial {...pricePartialProps} />}
      imgSource={getProductImageUrl(product, { format: ImageManipulationFormat.Jpeg, width: 365 })}
      onClick={onClick}
      year={specFormatter.formatProductValue(ProductSpecKey.ModelYear, product)}
      availability={renderAvailability(product)}
    />
  );
};

export default ProductTilePartial;
